// Third-party
import React from 'react';

// Components
import { ErrorBoundary } from '../errors';
import ContactLabel from '../misc/ContactLabel';
import DataTableV2 from '../data/DataTableV2/DataTableV2';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Legacy Component
import LegacyContactsTable, { LegacyContactsTableFields } from './LegacyContactsTable';

// Libraries
import classNames from 'classnames';
import { get } from 'lodash';

// Utils
import { CONTACT_LAYOUT } from '../../../utils/contacts';
import { formatEventDate } from '@/utils/time';
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { getValueColumnDef, getCustomColumnDef } from '@/utils/data-table';
import { getThemeVersion } from '@/utils/theme';
import { LoadingSpinner } from '../common';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import localStorageUtils from '@/utils/local-storage';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../utils/permissions';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

// Types
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { ColumnDef, ColumnSort } from '@tanstack/react-table';
import { MultiselectAction } from '@/types/data-table';
import { SortingField } from '../../../types/data';
import Contact from '@/types/contact';
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import withForwardedRef, { WithForwardedRefProps } from '../hocs/withForwardedRef';
import { SearchInput } from '../inputs';
import { PillLabel } from '../misc';

export enum ContactsTableFields {
  Category = 'category',
  ContactType = 'contactType',
  Email = 'email',
  Fname = 'fname',
  Fullname = 'fullname',
  Id = '_id',
  Lname = 'lname',
  Stage = 'stage',
  Uid = 'uid',
  updatedAt = 'updatedAt',
  planId = 'planId',
}

type Props = {
  className?: string;
  contacts: GenericContact[];
  hasMore?: boolean;
  layout?: CONTACT_LAYOUT;
  loading?: boolean;
  multiselect?: boolean; // See note below
  orderedFields?: ContactsTableFields[];
  persistenceSource?: string;
  searchable?: boolean;
  searchQuery?: string;
  selectable?: boolean;
  serverSideSorting?: boolean;
  showHeaderSection?: boolean;
  showDataSourceTabs?: boolean;
  sortedColumns?: ColumnSort[];
  title?: string;
  user?: User | null;
  isPostMessageActive: boolean;
  debouncedSearchContacts: () => void;
  onClick: (contact: GenericContact) => void;
  onContactsSearch?: (query: string) => void;
  onDelete?: (contacts: GenericContact[]) => void;
  onEdit?: (contact: GenericContact) => void;
  onLoadMore: () => void;
  onServerSideSorting?: (sort: ColumnSort) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

/*
  Note on the multiselection option.

  Everything is in place to use the multiselection feature in this file, but
  ** it is not used anymore in the ContactsTable rendered from the Contacts page **
  since the light mode update.

  Since ContactsTable is used elsewhere and we haven't revamped those places yet,
  I will leave all the multiselection options in place for now.
  Feel free to clean everything up when we are positive we won't need it anymore.
*/

type LegacyProps = {
  fields?: LegacyContactsTableFields[];
  isThemeLight?: boolean;
  selectOnRowClick?: boolean;
  showCheckboxes?: boolean;
  tableScrollAncestor?: Window | HTMLElement | string;
  onSortingChange: (rules: SortingField[]) => void;
};

type WrapperProps = Props & LegacyProps & WithForwardedRefProps;

type State = {
  activeDataSource: string;
};

class ContactsTableV2 extends React.Component<Props> {
  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.CONTACTS,
    this.props.isPostMessageActive,
  );

  readonly i18nBasePath = 'Contacts';

  static defaultProps = {
    searchable: true,
    selectable: true,
    searchQuery: '',
    onClick: () => {},
    onContactsSearch: () => {},
    orderedFields: [
      ContactsTableFields.Fullname,
      ContactsTableFields.Uid,
      ContactsTableFields.Category,
      ContactsTableFields.Stage,
      ContactsTableFields.ContactType,
      ContactsTableFields.updatedAt,
    ],
    layout: CONTACT_LAYOUT.CONTACT,
    showDataSourceTabs: true,
  };

  state: State = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
  };

  componentDidMount() {
    const { isPostMessageActive, showDataSourceTabs, user, debouncedSearchContacts, setIsActiveContactPostMessage } = this.props;

    if (user && showDataSourceTabs) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActiveContactPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, user, () => debouncedSearchContacts());
    }
  }

  onDeleteContactsMultiselectAction = (selectedContacts: Contact[], clearSelection: CallableFunction) => {
    const { onDelete } = this.props;
    if (onDelete) {
      onDelete(selectedContacts);
    }
  };

  getMultiselectActions = () => {
    const { onDelete, user } = this.props;

    const actions = [];

    if (onDelete && user && userHasPermission(user, PERMISSIONS.CONTACTS_DELETE)) {
      const action: MultiselectAction<Contact> = {
        icon: 'ds-delete',
        label: <FormattedMessage id="Contacts.Delete" />,
        onClick: (selectedRows: Contact[], clearSelection: CallableFunction) => {
          this.onDeleteContactsMultiselectAction(selectedRows, clearSelection);
        },
        isCurrentlyAllowed: (selectedRows: Contact[]) => selectedRows.every(contact => contact.availableActions == null || contact.availableActions.delete),
      };
      actions.push(action);
    }

    return actions;
  };

  onEditContactFromMenu = (contactId: string) => {
    const { onEdit, contacts } = this.props;
    const contact = contacts.find(c => c._id === contactId);
    if (contact && onEdit) {
      onEdit(contact);
    }
  };

  onDeleteContactFromMenu = (contactId: string) => {
    const { onDelete, contacts } = this.props;
    const contact = contacts.find(c => c._id === contactId);
    if (contact && onDelete) {
      onDelete([contact]);
    }
  };

  getMenuActions = () => {
    const { onDelete, onEdit, user } = this.props;

    const actions = [];

    const noDataIsDeletablOrEditable = this.props.contacts.every(
      contact => contact.availableActions && !contact.availableActions.delete && !contact.availableActions.edit,
    );

    if (noDataIsDeletablOrEditable) {
      return [];
    }

    const canEdit = onEdit != null && user != null && userHasPermission(user, PERMISSIONS.CONTACTS_EDIT);

    if (canEdit) {
      actions.push({
        key: 'edit-contact',
        className: 'edit-contact',

        icon: 'icon-ds-edit',
        isAllowed: (value: Contact) => value.availableActions == null || value.availableActions.edit,
        onClick: (value: Contact | undefined) => {
          if (!value) {
            return;
          }
          const { _id } = value;
          if (_id) {
            this.onEditContactFromMenu(_id);
          }
        },
        value: <FormattedMessage id="Contacts.Edit" />,
      });
    }

    const canDelete = onDelete != null && user != null && userHasPermission(user, PERMISSIONS.CONTACTS_DELETE);

    if (canDelete) {
      actions.push({
        key: 'delete-contact',
        className: 'delete-contact destructive',
        icon: 'icon-ds-delete',
        isAllowed: (value: Contact) => value.availableActions == null || value.availableActions.delete,
        onClick: (value: Contact | undefined) => {
          if (!value) {
            return;
          }
          const { _id } = value;
          if (_id) {
            this.onDeleteContactFromMenu(_id);
          }
        },
        value: <FormattedMessage id="Contacts.DeleteOne" />,
      });
    }

    return actions;
  };

  getTableData: () => Contact[] = () => {
    const { contacts, user } = this.props;

    const hasLastNamePermission = user && userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME);
    return contacts.map(contact => ({
      ...contact,
      [ContactsTableFields.Fullname]: `${contact.fname} ${hasLastNamePermission ? contact.lname : ''}`,
      [ContactsTableFields.updatedAt]: contact.updatedAt ? formatEventDate(new Date(contact.updatedAt)) : undefined,
    }));
  };

  getTableColumns = () => {
    const { contacts, orderedFields, layout, user } = this.props;

    const columns: ColumnDef<Contact>[] = [
      getValueColumnDef(ContactsTableFields.Uid, this.i18nBasePath, {
        header: formatMessage(`Contacts.Fields.${layout === CONTACT_LAYOUT.CLIENT ? 'ClientID' : 'ContactID'}`),
      }),

      getCustomColumnDef(ContactsTableFields.Fullname, this.i18nBasePath, info => <ContactLabel contact={info.row.original} />, {
        header: formatMessage('Contacts.Headers.fullname'),
        meta: {
          serverSideSortKey: ContactsTableFields.Fname,
        },
      }),

      getValueColumnDef(ContactsTableFields.Fname, this.i18nBasePath),
    ];

    const dataHasUpdatedAt = contacts.some(contact => contact.updatedAt != null);
    if (dataHasUpdatedAt) {
      columns.push(getValueColumnDef(ContactsTableFields.updatedAt, this.i18nBasePath));
    }

    if (user && userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME)) {
      columns.push(getValueColumnDef(ContactsTableFields.Lname, this.i18nBasePath));
    }

    if (user && userHasPermission(user, PERMISSIONS.CONTACTS_EMAIL)) {
      columns.push(getValueColumnDef(ContactsTableFields.Email, this.i18nBasePath));
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.CONTACTS_CATEGORY)) {
      columns.push(
        getValueColumnDef(ContactsTableFields.Category, this.i18nBasePath, undefined, value =>
          !value ? <></> : <span>{formatMessage(`Contacts.Categories.${value}`)}</span>,
        ),
      );
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.CONTACTS_STAGE)) {
      columns.push(
        getValueColumnDef(ContactsTableFields.Stage, this.i18nBasePath, undefined, value =>
          !value ? <></> : <span>{formatMessage(`Contacts.ContactStages.${value}`)}</span>,
        ),
      );
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.CONTACTS_TYPE)) {
      columns.push(
        getValueColumnDef(ContactsTableFields.ContactType, this.i18nBasePath, undefined, value =>
          !value ? <></> : <span>{formatMessage(`Contacts.ContactTypes.${value}`)}</span>,
        ),
      );
    }

    if (user && userHasPermissionEnabled(user, PERMISSIONS.PLANNING)) {
      columns.push(
        getValueColumnDef(ContactsTableFields.planId, this.i18nBasePath, undefined, value =>
          !value ? <></> : <PillLabel className="pill-label-v2 green" text={formatMessage('Contacts.Linked')} />,
        ),
      );
    }

    return !orderedFields ? columns : orderedFields.map(field => columns.find(column => column.id === field)).filter(column => column != null);
  };

  onContactClick = (row: Contact) => {
    const { contacts } = this.props;
    const id = get(row, '_id');

    const contact = contacts.find(c => c._id === id);
    if (!contact) {
      return;
    }

    this.props.onClick(contact);
  };

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, debouncedSearchContacts, setIsActiveContactPostMessage, setPersistenceSource } = this.props;

    if (!user || !this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    debouncedSearchContacts();
  };

  render() {
    const {
      className,
      multiselect,
      searchable,
      searchQuery,
      layout,
      loading,
      title,
      showDataSourceTabs,
      showHeaderSection,
      serverSideSorting,
      sortedColumns,
      onServerSideSorting,
    } = this.props;
    const { activeDataSource } = this.state;

    const placeholder = layout === CONTACT_LAYOUT.CLIENT ? formatMessage('Contacts.Search.PlaceholderClient') : formatMessage('Contacts.Search.Placeholder');
    const tableColumns = this.getTableColumns();

    return (
      <div className={classNames('table-container contacts-table-v2 d-flex flex-column', className)}>
        <ErrorBoundary>
          {showHeaderSection && (
            <div className="contacts-header d-flex align-items-start">
              <div className="flex-fill" data-private data-dd-privacy="mask">
                {title && <h1>{title}</h1>}
              </div>

              {searchable && (
                <SearchInput
                  className="contact-search"
                  placeholder={placeholder}
                  onChange={this.props.onContactsSearch}
                  value={searchQuery}
                  data-private
                  data-dd-privacy="mask"
                />
              )}
            </div>
          )}

          {this.dataSourceTabs && showDataSourceTabs && (
            <div className="tabs-container">
              <DataSourceTabs disabled={loading} tabs={this.dataSourceTabs} value={activeDataSource} onChange={this.onSelectDataSource} />
            </div>
          )}

          {loading && (
            <div className="loading-spinner-container">
              <LoadingSpinner message={formatMessage('Contacts.Loading')} />
            </div>
          )}

          {!loading && (
            <DataTableV2
              columns={tableColumns}
              data={this.getTableData()}
              enableColumnFreeze={false}
              enableMultiselect={multiselect}
              enableSorting
              enableTableSelection={this.props.selectable}
              infiniteScrollOptions={{
                show: this.props.hasMore ?? false,
                onEnter: this.props.onLoadMore,
              }}
              menuItems={this.getMenuActions()}
              multiselectActionsOptions={{
                actions: this.getMultiselectActions(),
                selectedTitle: 'Contacts.SelectedTitle',
              }}
              noDataComponent={() => <span>{formatMessage('ContactsTable.NoRows')}</span>}
              onRowClick={this.onContactClick}
              onServerSideSorting={sort => onServerSideSorting && onServerSideSorting(sort)}
              serverSideSorting={serverSideSorting}
              sortedColumns={sortedColumns}
              tableTestId="contacts-list"
            />
          )}
        </ErrorBoundary>
      </div>
    );
  }
}

class ContactsTable extends React.Component<WrapperProps> {
  render() {
    const { isLightVersion } = getThemeVersion(this.props.user);
    if (isLightVersion) {
      return <ContactsTableV2 {...this.props} ref={this.props.forwardedRef} />;
    }
    return <LegacyContactsTable {...this.props} ref={this.props.forwardedRef} />;
  }
}

export default withForwardedRef(ContactsTable);
